(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-ct-native-reward]';
  const STYLES_SELECTOR = '[data-ct-native-reward-styles]';
  const CAMPAIGN_CLASS = 'ct-cw-campaign-active';
  const APPLIED_PARAM = 'ct_reward_applied';

  // UX16: protect the real adapter choices on the two approved stock accessories.
  const STOCK_ADAPTER_CLASS = 'ct-stock-adapter-in-view';
  const STOCK_ADAPTER_PRODUCT_IDS = new Set(['10222054178850', '10222054146082']);
  let stockAdapterController = null;

  const stockAdapterProductInfo = () => {
    if (!document.body.classList.contains('ct-stock-pdp')) return null;
    const productInfo = document.querySelector('product-info[data-ct-stock-pdp-scope="v1"]');
    return productInfo && STOCK_ADAPTER_PRODUCT_IDS.has(productInfo.dataset.productId) ? productInfo : null;
  };

  const stockAdapterFocusTarget = () => {
    const productInfo = stockAdapterProductInfo();
    if (!productInfo || document.querySelector('dialog[open]')) return null;
    const radios = [
      ...productInfo.querySelectorAll('[data-ct-adapter-addon] input[data-ct-adapter-option]:checked:not(:disabled)'),
      ...productInfo.querySelectorAll('[data-ct-adapter-addon] input[data-ct-adapter-option]:not(:disabled)'),
    ];
    return radios.find((target) => {
      if (!(target instanceof HTMLElement) || !target.isConnected ||
        target.matches('[aria-disabled="true"]') || target.closest('[inert], [hidden], [aria-hidden="true"]')) return false;
      const rect = target.getBoundingClientRect();
      for (let ancestor = target; ancestor instanceof HTMLElement; ancestor = ancestor.parentElement) {
        const style = getComputedStyle(ancestor);
        if (style.display === 'none' || style.visibility !== 'visible' || Number(style.opacity) === 0) return false;
      }
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      if (rect.width <= 0 || rect.height <= 0 || x <= 0 || x >= window.innerWidth ||
        y <= 0 || y >= window.innerHeight) return false;
      const hit = document.elementFromPoint(x, y);
      const label = target.closest('label');
      return hit === target || !!(label && hit && label.contains(hit));
    }) || null;
  };

  const resetStockAdapterFloatGuard = () => {
    if (stockAdapterController) {
      stockAdapterController.observer?.disconnect();
      stockAdapterController.mobileViewport.removeEventListener?.('change', stockAdapterController.onViewportChange);
      stockAdapterController = null;
    }
    document.body.classList.remove(STOCK_ADAPTER_CLASS);
  };

  const initStockAdapterFloatGuard = () => {
    const productInfo = stockAdapterProductInfo();
    const addons = productInfo ? [...productInfo.querySelectorAll('[data-ct-adapter-addon]')] : [];
    if (stockAdapterController && stockAdapterController.productInfo === productInfo &&
      addons.length === stockAdapterController.addons.length &&
      addons.every((addon, index) => addon === stockAdapterController.addons[index] && addon.isConnected)) return;
    resetStockAdapterFloatGuard();
    if (!addons.length) return;

    const controller = {
      productInfo, addons, observer: null,
      mobileViewport: window.matchMedia('(max-width: 749px)'),
      intersections: new Map(addons.map((addon) => [addon, true])),
      onViewportChange: null,
    };
    stockAdapterController = controller;

    const preserveLauncherFocus = (active = document.activeElement) => {
      if (stockAdapterController !== controller || !controller.mobileViewport.matches ||
        !document.body.classList.contains(STOCK_ADAPTER_CLASS) || document.querySelector('dialog[open]')) return;
      if (!(active instanceof HTMLElement) ||
        !active.closest('.ct-global-whatsapp-float, .ct-native-reward--product .ct-native-reward__launcher')) return;
      stockAdapterFocusTarget()?.focus({ preventScroll: true });
    };

    const updateVisibility = (intersects) => {
      if (stockAdapterController !== controller) return;
      const active = document.activeElement;
      document.body.classList.toggle(STOCK_ADAPTER_CLASS, intersects);
      if (intersects) preserveLauncherFocus(active);
    };

    // Default safe until every target's first observation; unsupported IO stays hidden on mobile.
    updateVisibility(true);
    controller.onViewportChange = () => preserveLauncherFocus();
    controller.mobileViewport.addEventListener?.('change', controller.onViewportChange);
    if (typeof window.IntersectionObserver !== 'function') return;
    try {
      controller.observer = new IntersectionObserver((entries) => {
        if (stockAdapterController !== controller) return;
        if (!controller.productInfo.isConnected) {
          initStockAdapterFloatGuard();
          return;
        }
        for (const entry of entries) {
          if (controller.intersections.has(entry.target)) {
            controller.intersections.set(entry.target, entry.target.isConnected && entry.isIntersecting);
          }
        }
        updateVisibility(controller.addons.some((addon) => addon.isConnected && controller.intersections.get(addon)));
      }, { threshold: 0 });
      addons.forEach((addon) => controller.observer.observe(addon));
    } catch {
      controller.observer?.disconnect();
      controller.observer = null;
      updateVisibility(true);
    }
  };

  document.addEventListener('product-info:loaded', initStockAdapterFloatGuard);
  document.addEventListener('shopify:section:load', initStockAdapterFloatGuard);
  document.addEventListener('shopify:section:unload', (event) => {
    if (stockAdapterController && event.target instanceof Element &&
      event.target.contains(stockAdapterController.productInfo)) resetStockAdapterFloatGuard();
  });
  window.addEventListener('pagehide', resetStockAdapterFloatGuard);
  window.addEventListener('pageshow', initStockAdapterFloatGuard);

  const init = (root) => {
    if (root.dataset.ctInitialized === 'true') return;

    const dialog = root.querySelector('[data-ct-reward-dialog]');
    const openButton = root.querySelector('[data-ct-reward-open]');
    const dismissButton = root.querySelector('[data-ct-reward-dismiss]');
    const form = root.querySelector('#CtNativeRewardForm');
    const confirmation = root.querySelector('[data-ct-reward-confirmation]');
    const fallbackAction = root.querySelector('[data-ct-reward-action]');
    let autoOpenTarget = dialog?.querySelector('[data-ct-reward-auto-open]');

    if (!dialog || !openButton || !dismissButton) return;
    root.dataset.ctInitialized = 'true';

    const campaignIsActive = () => document.documentElement.classList.contains(CAMPAIGN_CLASS);

    const open = (focusTarget = null) => {
      if (campaignIsActive() || root.hidden) return;

      root.dataset.open = 'true';
      openButton.setAttribute('aria-expanded', 'true');

      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }

      if (focusTarget instanceof HTMLElement) {
        requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
      }
    };

    const close = ({ restoreFocus = true } = {}) => {
      root.dataset.open = 'false';
      openButton.setAttribute('aria-expanded', 'false');

      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');

      if (restoreFocus && document.contains(openButton) && !campaignIsActive() && !root.hidden) {
        if (document.body.classList.contains('ct-stock-pdp') && getComputedStyle(openButton).visibility === 'hidden') {
          const focusTargets = [
            document.querySelector('product-info[data-ct-stock-pdp-scope="v1"] h1'),
            document.querySelector('product-info[data-ct-stock-pdp-scope="v1"] .shopify-payment-button__button--unbranded'),
            document.querySelector('header .header__heading-link'),
            stockAdapterFocusTarget(),
          ];
          for (const target of focusTargets) {
            if (!(target instanceof HTMLElement) || target.matches(':disabled, [aria-disabled="true"]')) continue;
            const rect = target.getBoundingClientRect();
            const style = getComputedStyle(target);
            if (style.display === 'none' || style.visibility !== 'visible' || Number(style.opacity) === 0 ||
              rect.width <= 0 || rect.height <= 0 || rect.bottom <= 0 || rect.top >= window.innerHeight ||
              rect.right <= 0 || rect.left >= window.innerWidth) continue;
            if (target.tagName === 'H1' && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
            target.focus({ preventScroll: true });
            if (document.activeElement === target) break;
          }
        } else {
          openButton.focus({ preventScroll: true });
        }
      }
    };

    const dismiss = () => {
      close({ restoreFocus: false });
      root.hidden = true;
      root.dataset.dismissed = 'true';
    };

    const currentDestination = () => {
      const destination = new URL(window.location.href);
      destination.hash = '';
      destination.searchParams.delete('customer_posted');
      destination.searchParams.delete('form_type');
      destination.searchParams.delete(APPLIED_PARAM);

      const productForm = document.querySelector(
        'form[id^="product-form-"], form[data-type="add-to-cart-form"]'
      );
      const variantInput = productForm?.querySelector('input[name="id"]');

      if (destination.pathname.includes('/products/') && variantInput?.value) {
        destination.searchParams.set('variant', variantInput.value);
      }

      destination.searchParams.set(APPLIED_PARAM, '1');
      return `${destination.pathname}${destination.search}`;
    };

    const discountDestination = () => {
      const discountUrl = new URL(
        root.dataset.ctRewardDiscountUrl || fallbackAction?.href || '/discount/CHETIENDA55',
        window.location.origin
      );
      discountUrl.searchParams.set('redirect', currentDestination());
      return `${discountUrl.pathname}${discountUrl.search}`;
    };

    const updateDestinations = () => {
      const destination = discountDestination();
      const returnToInput = form?.querySelector('input[name="return_to"]');

      if (returnToInput instanceof HTMLInputElement) returnToInput.value = destination;
      if (fallbackAction instanceof HTMLAnchorElement) fallbackAction.href = destination;

      return destination;
    };

    const consumeAppliedMarker = () => {
      const currentUrl = new URL(window.location.href);
      if (currentUrl.searchParams.get(APPLIED_PARAM) !== '1' || !confirmation) return false;

      currentUrl.searchParams.delete(APPLIED_PARAM);
      currentUrl.searchParams.delete('customer_posted');
      currentUrl.searchParams.delete('form_type');
      window.history.replaceState(
        window.history.state,
        '',
        `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
      );

      if (form instanceof HTMLFormElement) form.hidden = true;
      confirmation.hidden = false;
      dialog.setAttribute('aria-labelledby', 'CtNativeRewardAppliedTitle');
      autoOpenTarget = confirmation;
      return true;
    };

    openButton.addEventListener('click', () => open());
    dismissButton.addEventListener('click', dismiss);
    form?.addEventListener('submit', updateDestinations);

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });

    dialog.addEventListener('close', () => {
      root.dataset.open = 'false';
      openButton.setAttribute('aria-expanded', 'false');
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });

    let stylesRevealed = false;
    const revealAfterStyles = () => {
      if (stylesRevealed) return;

      stylesRevealed = true;
      root.dataset.ctStylesReady = 'true';

      if (autoOpenTarget) requestAnimationFrame(() => open(autoOpenTarget));
    };

    consumeAppliedMarker();

    if (fallbackAction instanceof HTMLAnchorElement) {
      window.location.replace(updateDestinations());
      return;
    }

    const styles = document.querySelector(STYLES_SELECTOR);
    if (!styles || styles.dataset.ctReady === 'true' || styles.media === 'all') {
      revealAfterStyles();
    } else {
      styles.addEventListener('load', revealAfterStyles, { once: true });
      styles.addEventListener('error', revealAfterStyles, { once: true });
    }

    const campaignObserver = new MutationObserver(() => {
      if (campaignIsActive() && dialog.open) close({ restoreFocus: false });
    });
    campaignObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  };

  const initAll = (scope = document) => {
    initStockAdapterFloatGuard();
    scope.querySelectorAll(ROOT_SELECTOR).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
})();

